import React, {useEffect, useState} from 'react';
import {ICategory, Tag} from '../../model/ICategory';
import {IAccount} from '../../model/IAccount';
import {ICurrency} from '../../model/ICurrency';
import {TransactionType} from '../../model/TransactionType';
import {
    AggregationFunction,
    CurrencyMode,
    IStatisticModel,
    IStatisticModelWrite,
    PeriodType,
    StatisticTransactionType,
} from '../../model/IStats';
import {categoryApi} from '../../api/CategoriesService';
import {accountApi} from '../../api/AccountsService';
import CategoriesPicker from '../ui/transaction/CategoriesPicker';
import TagsPicker from '../ui/transaction/TagsPicker';
import AccountsPicker from '../ui/transaction/AccountsPicker';
import CurrencyPicker from '../ui/account/CurrencyPicker';
import SimpleDatePicker from '../ui/date_picker/SimpleDatePicker';
import {
    AGGREGATION_FUNCTION_OPTIONS,
    CURRENCY_MODE_OPTIONS,
    MONTH_OPTIONS,
    PERIOD_TYPE_OPTIONS,
    pickByIds,
} from '../../utils/statsModels';
import * as common from '../../utils/common';

interface StatsModelFormProps {
    initial?: IStatisticModel;
    submitting: boolean;
    submitLabel: string;
    onSubmit: (body: IStatisticModelWrite) => void;
    onCancel: () => void;
}

const selectClassName = 'mt-1 bg-white border border-gray-300 rounded-md shadow-sm sm:text-sm';
const labelClassName = 'block text-sm font-medium text-gray-700';
const inputClassName = 'mt-1 bg-white border border-gray-300 rounded-md shadow-sm sm:text-sm';

const StatsModelForm: React.FunctionComponent<StatsModelFormProps> = ({
    initial,
    submitting,
    submitLabel,
    onSubmit,
    onCancel,
}) => {
    const now = new Date();
    const [name, setName] = useState(initial?.name ?? '');
    const [transactionType, setTransactionType] = useState<StatisticTransactionType>(
        initial?.transaction_type ?? TransactionType.Expense
    );
    const [periodType, setPeriodType] = useState<PeriodType>(initial?.period_type ?? 'month');
    const [defaultYear, setDefaultYear] = useState<number | ''>(initial?.default_year ?? now.getFullYear());
    const [defaultMonth, setDefaultMonth] = useState<number | ''>(initial?.default_month ?? now.getMonth() + 1);
    const [defaultNMonths, setDefaultNMonths] = useState<number | ''>(initial?.default_n_months ?? 3);
    const [defaultDateFrom, setDefaultDateFrom] = useState(initial?.default_date_from ?? common.getFirstDayOfCurrentMonthDate());
    const [defaultDateTo, setDefaultDateTo] = useState(initial?.default_date_to ?? common.trunc(new Date()));
    const [currencyMode, setCurrencyMode] = useState<CurrencyMode>(initial?.currency_mode ?? 'multi');
    const [currency, setCurrency] = useState<ICurrency | null>(initial?.currency ?? null);
    const [groupByCategory, setGroupByCategory] = useState(initial?.group_by_category ?? false);
    const [groupByMonth, setGroupByMonth] = useState(initial?.group_by_month ?? false);
    const [aggregationFunction, setAggregationFunction] = useState<AggregationFunction>(initial?.aggregation_function ?? 'sum');
    const [includeCategoryIds, setIncludeCategoryIds] = useState<number[]>(initial?.include_categories ?? []);
    const [excludeCategoryIds, setExcludeCategoryIds] = useState<number[]>(initial?.exclude_categories ?? []);
    const [groupCategoryIds, setGroupCategoryIds] = useState<number[]>(initial?.group_categories ?? []);
    const [includeTagIds, setIncludeTagIds] = useState<number[]>(initial?.include_tags ?? []);
    const [excludeTagIds, setExcludeTagIds] = useState<number[]>(initial?.exclude_tags ?? []);
    const [includeAccountIds, setIncludeAccountIds] = useState<number[]>(initial?.include_accounts ?? []);
    const [excludeAccountIds, setExcludeAccountIds] = useState<number[]>(initial?.exclude_accounts ?? []);

    const {data: allCategories} = categoryApi.useFetchAllCategoriesQuery(transactionType);
    const {data: tagsList} = categoryApi.useFetchAllTagsQuery();
    const {data: accounts} = accountApi.useFetchAllAccountsQuery();
    const {data: currencies} = categoryApi.useFetchAllCurrenciesQuery();

    useEffect(() => {
        if (!initial) {
            return;
        }
        setName(initial.name);
        setTransactionType(initial.transaction_type);
        setPeriodType(initial.period_type);
        setDefaultYear(initial.default_year ?? '');
        setDefaultMonth(initial.default_month ?? '');
        setDefaultNMonths(initial.default_n_months ?? '');
        setDefaultDateFrom(initial.default_date_from ?? common.getFirstDayOfCurrentMonthDate());
        setDefaultDateTo(initial.default_date_to ?? common.trunc(new Date()));
        setCurrencyMode(initial.currency_mode);
        setCurrency(initial.currency);
        setGroupByCategory(initial.group_by_category);
        setGroupByMonth(initial.group_by_month);
        setAggregationFunction(initial.aggregation_function);
        setIncludeCategoryIds(initial.include_categories);
        setExcludeCategoryIds(initial.exclude_categories);
        setGroupCategoryIds(initial.group_categories);
        setIncludeTagIds(initial.include_tags);
        setExcludeTagIds(initial.exclude_tags);
        setIncludeAccountIds(initial.include_accounts);
        setExcludeAccountIds(initial.exclude_accounts);
    }, [initial]);

    useEffect(() => {
        if ((currencyMode === 'fixed' || currencyMode === 'parameterized') && !currency && currencies?.length) {
            setCurrency(currencies[0]);
        }
    }, [currencyMode, currency, currencies]);

    const includeCategories = pickByIds(allCategories, includeCategoryIds);
    const excludeCategories = pickByIds(allCategories, excludeCategoryIds);
    const groupCategories = pickByIds(allCategories, groupCategoryIds);
    const includeTags = pickByIds(tagsList, includeTagIds);
    const excludeTags = pickByIds(tagsList, excludeTagIds);
    const includeAccounts = pickByIds(accounts, includeAccountIds);
    const excludeAccounts = pickByIds(accounts, excludeAccountIds);

    const handleTransactionTypeChange = (nextType: StatisticTransactionType) => {
        setTransactionType(nextType);
        setIncludeCategoryIds([]);
        setExcludeCategoryIds([]);
        setGroupCategoryIds([]);
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        onSubmit({
            name: name.trim(),
            transaction_type: transactionType,
            period_type: periodType,
            default_year: defaultYear === '' ? null : Number(defaultYear),
            default_month: defaultMonth === '' ? null : Number(defaultMonth),
            default_n_months: defaultNMonths === '' ? null : Number(defaultNMonths),
            default_date_from: periodType === 'date_range' ? defaultDateFrom : null,
            default_date_to: periodType === 'date_range' ? defaultDateTo : null,
            currency_mode: currencyMode,
            currency_id: currencyMode === 'multi' ? null : (currency?.id ?? null),
            group_by_category: groupByCategory,
            group_by_month: groupByMonth,
            aggregation_function: aggregationFunction,
            include_categories: includeCategoryIds,
            exclude_categories: excludeCategoryIds,
            group_categories: groupByCategory ? groupCategoryIds : [],
            include_tags: includeTagIds,
            exclude_tags: excludeTagIds,
            include_accounts: includeAccountIds,
            exclude_accounts: excludeAccountIds,
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-8 pb-10">
            <section>
                <h2 className="text-sm font-semibold text-gray-900 mb-3">General</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className={labelClassName} htmlFor="stats-model-name">Name</label>
                        <input
                            id="stats-model-name"
                            required
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            className={`${inputClassName} w-full`}
                        />
                    </div>
                    <div>
                        <span className={labelClassName}>Transaction type</span>
                        <div className="mt-1 inline-flex" role="radiogroup" aria-label="Transaction type">
                            <button
                                type="button"
                                className={`${transactionType === TransactionType.Expense ? 'bg-gray-300 text-indigo-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} px-3 py-1 text-xs font-medium rounded-l-full`}
                                onClick={() => handleTransactionTypeChange(TransactionType.Expense)}
                            >
                                Expense
                            </button>
                            <button
                                type="button"
                                className={`${transactionType === TransactionType.Income ? 'bg-gray-300 text-indigo-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'} px-3 py-1 text-xs font-medium rounded-r-full`}
                                onClick={() => handleTransactionTypeChange(TransactionType.Income)}
                            >
                                Income
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section>
                <h2 className="text-sm font-semibold text-gray-900 mb-3">Period</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className={labelClassName} htmlFor="stats-model-period">Period type</label>
                        <select
                            id="stats-model-period"
                            className={`${selectClassName} w-full`}
                            value={periodType}
                            onChange={(event) => setPeriodType(event.target.value as PeriodType)}
                        >
                            {PERIOD_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    {periodType === 'month' && (
                        <>
                            <div>
                                <label className={labelClassName} htmlFor="stats-model-year">Default year</label>
                                <input
                                    id="stats-model-year"
                                    type="number"
                                    className={`${inputClassName} w-full`}
                                    value={defaultYear}
                                    onChange={(event) => setDefaultYear(event.target.value === '' ? '' : Number(event.target.value))}
                                />
                            </div>
                            <div>
                                <label className={labelClassName} htmlFor="stats-model-month">Default month</label>
                                <select
                                    id="stats-model-month"
                                    className={`${selectClassName} w-full`}
                                    value={defaultMonth}
                                    onChange={(event) => setDefaultMonth(event.target.value === '' ? '' : Number(event.target.value))}
                                >
                                    <option value="">Not set</option>
                                    {MONTH_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                    {periodType === 'last_n_months' && (
                        <div>
                            <label className={labelClassName} htmlFor="stats-model-n-months">Default N months</label>
                            <input
                                id="stats-model-n-months"
                                type="number"
                                min={1}
                                className={`${inputClassName} w-full`}
                                value={defaultNMonths}
                                onChange={(event) => setDefaultNMonths(event.target.value === '' ? '' : Number(event.target.value))}
                            />
                        </div>
                    )}
                    {periodType === 'date_range' && (
                        <>
                            <div>
                                <label className={labelClassName}>Default date from</label>
                                <SimpleDatePicker date={defaultDateFrom} setDate={setDefaultDateFrom} className="mt-1"/>
                            </div>
                            <div>
                                <label className={labelClassName}>Default date to</label>
                                <SimpleDatePicker date={defaultDateTo} setDate={setDefaultDateTo} className="mt-1"/>
                            </div>
                        </>
                    )}
                </div>
            </section>

            <section>
                <h2 className="text-sm font-semibold text-gray-900 mb-3">Currency</h2>
                <div className="flex flex-row items-end flex-wrap">
                    <div>
                        <label className={labelClassName} htmlFor="stats-model-currency-mode">Currency mode</label>
                        <select
                            id="stats-model-currency-mode"
                            className={`${selectClassName} w-56`}
                            value={currencyMode}
                            onChange={(event) => setCurrencyMode(event.target.value as CurrencyMode)}
                        >
                            {CURRENCY_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    {(currencyMode === 'fixed' || currencyMode === 'parameterized') && currencies && (
                        <CurrencyPicker
                            label={currencyMode === 'parameterized' ? 'Default currency' : 'Currency'}
                            currency={currency}
                            setCurrency={setCurrency}
                            currencies={currencies}
                            smallerSize
                        />
                    )}
                </div>
            </section>

            <section>
                <h2 className="text-sm font-semibold text-gray-900 mb-3">Aggregation</h2>
                <div className="flex flex-row items-center flex-wrap gap-6">
                    <label className="flex items-center text-sm text-gray-700">
                        <input
                            type="checkbox"
                            className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            checked={groupByCategory}
                            onChange={(event) => setGroupByCategory(event.target.checked)}
                        />
                        <span className="ml-2">Group by category</span>
                    </label>
                    <label className="flex items-center text-sm text-gray-700">
                        <input
                            type="checkbox"
                            className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                            checked={groupByMonth}
                            onChange={(event) => setGroupByMonth(event.target.checked)}
                        />
                        <span className="ml-2">Group by month</span>
                    </label>
                    <div>
                        <label className={labelClassName} htmlFor="stats-model-agg">Function</label>
                        <select
                            id="stats-model-agg"
                            className={selectClassName}
                            value={aggregationFunction}
                            onChange={(event) => setAggregationFunction(event.target.value as AggregationFunction)}
                        >
                            {AGGREGATION_FUNCTION_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                {aggregationFunction === 'avg' && periodType !== 'last_n_months' && (
                    <p className="mt-2 text-xs text-gray-500">Average is applied across last N months. Other period types use sum.</p>
                )}
            </section>

            <section>
                <h2 className="text-sm font-semibold text-gray-900 mb-3">Filters</h2>
                <div className="flex flex-row items-center flex-wrap">
                    {allCategories && (
                        <CategoriesPicker
                            label="Categories"
                            categories={includeCategories}
                            setCategories={(categories: ICategory[]) => setIncludeCategoryIds(categories.map((category) => category.id))}
                            allCategories={allCategories}
                            className="mt-2 mx-4"
                        />
                    )}
                    {allCategories && (
                        <CategoriesPicker
                            label="Excluded categories"
                            categories={excludeCategories}
                            setCategories={(categories: ICategory[]) => setExcludeCategoryIds(categories.map((category) => category.id))}
                            allCategories={allCategories}
                            className="mt-2 mx-4"
                        />
                    )}
                </div>
                <div className="flex flex-row items-center flex-wrap">
                    {accounts && (
                        <AccountsPicker
                            label="Accounts"
                            accounts={includeAccounts}
                            setAccounts={(nextAccounts: IAccount[]) => setIncludeAccountIds(nextAccounts.map((account) => account.id))}
                            allAccounts={accounts}
                            className="mt-2 mx-4"
                        />
                    )}
                    {accounts && (
                        <AccountsPicker
                            label="Excluded accounts"
                            accounts={excludeAccounts}
                            setAccounts={(nextAccounts: IAccount[]) => setExcludeAccountIds(nextAccounts.map((account) => account.id))}
                            allAccounts={accounts}
                            className="mt-2 mx-4"
                        />
                    )}
                </div>
                <div className="flex flex-row items-center flex-wrap">
                    {tagsList && (
                        <TagsPicker
                            label="Tags"
                            tags={includeTags}
                            setTags={(tags: Tag[]) => setIncludeTagIds(tags.map((tag) => tag.id))}
                            tagsList={tagsList}
                            className="mt-2 mx-4"
                        />
                    )}
                    {tagsList && (
                        <TagsPicker
                            label="Excluded tags"
                            tags={excludeTags}
                            setTags={(tags: Tag[]) => setExcludeTagIds(tags.map((tag) => tag.id))}
                            tagsList={tagsList}
                            className="mt-2 mx-4"
                        />
                    )}
                </div>
                {groupByCategory && allCategories && (
                    <div className="flex flex-row items-center flex-wrap mt-2">
                        <CategoriesPicker
                            label="Group categories"
                            categories={groupCategories}
                            setCategories={(categories: ICategory[]) => setGroupCategoryIds(categories.map((category) => category.id))}
                            allCategories={allCategories}
                            className="mt-2 mx-4"
                        />
                    </div>
                )}
            </section>

            <div className="flex items-center gap-3">
                <button
                    type="submit"
                    disabled={submitting || !name.trim()}
                    className="py-2 px-6 border border-transparent text-sm font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200 focus:outline-none disabled:opacity-50"
                >
                    {submitLabel}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    className="py-2 px-6 border border-transparent text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 focus:outline-none"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
};

export default StatsModelForm;
